import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { produce } from "immer";
import { defineStore } from "pinia";

import { AI_AGENTS_MESSAGE_SCHEMA_VERSION } from "@/helpers/constants/aiAgents";
import { usePromiseWrapper } from "@/hooks/common";
import { aiAgentsApiService } from "@/services/modules/aiAgents";
import optionsApiService from "@/services/modules/options";
import { useFiles } from "@/stores/ai/files";

import type {
  AgentSessionInfo,
  AiAgent,
  AiAgentBase,
  CreateAgentSessionPayload,
  CreateAiAgentPayload,
  MessageContent,
  MessageContentBlock,
  UpdateAiAgentPayload,
} from "@/models/aiAgents";
import type { IResponseMeta } from "@/models/common";
import type { AiAgentsOptions } from "@/models/options";
import type { Ref } from "vue";

interface AiAgentsStore {
  aiAgents: Ref<AiAgentBase[]>;
  isAiAgentsRequestLoading: Ref<boolean>;
  searchMeta: Ref<IResponseMeta | undefined>;
  getAllIsLoading: Ref<boolean>;
  options: Ref<AiAgentsOptions | undefined>;
  agentSessionInfo: Ref<AgentSessionInfo>;
  getAll: (params?: Record<string, unknown>) => Promise<AiAgentBase[]>;
  create: (payload: CreateAiAgentPayload) => Promise<AiAgentBase>;
  update: (
    id: AiAgent["id"],
    payload: UpdateAiAgentPayload
  ) => Promise<AiAgentBase>;
  deleteById: (id: AiAgent["id"]) => Promise<void>;
  createAgentSession: (payload: CreateAgentSessionPayload) => Promise<number>;
  promptAgent: ({
    prompt,
    files,
    sessionId,
  }: {
    prompt: string;
    files: File[];
    sessionId: number;
  }) => Promise<void>;
  getAgentSession: (session_id: string) => Promise<void>;
  getAllAgentSessions: (params: { agent_id: string }) => Promise<void>;
  getAllAgentsSessions: () => Promise<void>;
  clearAgentSessionInfo: () => void;
}

const buildMessageContent = (
  prompt: string,
  fileBlocks: MessageContentBlock[] = []
): MessageContent => {
  const contentBlocks: MessageContentBlock[] = [];

  if (prompt.trim()) {
    contentBlocks.push(createTextBlock(prompt));
  }

  contentBlocks.push(...fileBlocks);

  return {
    version: AI_AGENTS_MESSAGE_SCHEMA_VERSION,
    content: contentBlocks,
  };
};

const createTextBlock = (text: string): MessageContentBlock => ({
  type: "text",
  text: text.trim(),
});

export const useAiAgentsStore = defineStore("aiAgents", (): AiAgentsStore => {
  const getAgentSessionInitialState = (): AgentSessionInfo => ({
    abortControllers: {
      searchDeals: new AbortController(),
    },
    activeSession: null,
    agent: null,
    deal: null,
    htmlSearchContent: "",
    htmlSearchContentCopy: "",
    isPinned: false,
    isPromptingAgent: false,
    isSendingPrompt: false,
    isShown: false,
    isSubmittingManually: false,
    previous_sessions: [],
    prompt: "",
    requestIsLoading: false,
  });
  const aiAgents = ref<AiAgentBase[]>([]);
  const searchMeta = ref<IResponseMeta>();
  const isAiAgentsRequestLoading = ref(false);
  const options = ref<AiAgentsOptions>();
  const agentSessionInfo = ref<AgentSessionInfo>(getAgentSessionInitialState());

  const { uploadFiles } = useFiles();
  const { t } = useI18n();

  const { fetchWrapper: getAll, loading: getAllIsLoading } = usePromiseWrapper(
    async (params?: Record<string, unknown>) => {
      isAiAgentsRequestLoading.value = true;
      const { data, meta } = await aiAgentsApiService.getAll(params);

      aiAgents.value = data;
      searchMeta.value = meta;

      return aiAgents.value;
    },
    {
      finally: () => {
        isAiAgentsRequestLoading.value = false;
      },
    }
  );

  const { fetchWrapper: create } = usePromiseWrapper(
    async (payload: CreateAiAgentPayload) => {
      const createdAgent = await aiAgentsApiService.createAIAgent(payload);
      aiAgents.value.unshift(createdAgent);

      return createdAgent;
    },
    {
      onError: (error) => {
        throw error; // Rethrow for validation
      },
    }
  );

  const { fetchWrapper: update } = usePromiseWrapper(
    async (id: AiAgent["id"], payload: UpdateAiAgentPayload) => {
      const updatedAgent = await aiAgentsApiService.update(id, payload);

      aiAgents.value = produce(aiAgents.value, (draft) => {
        const index = draft.findIndex((agent) => agent.id === updatedAgent.id);
        if (index > -1) {
          draft[index] = updatedAgent;
        }
      });

      return updatedAgent;
    },
    {
      onError: (error) => {
        throw error; // Rethrow for validation
      },
    }
  );

  const { fetchWrapper: deleteById } = usePromiseWrapper(
    async (id: AiAgent["id"]) => {
      await aiAgentsApiService.deleteById(id);
      aiAgents.value = aiAgents.value.filter((agent) => agent.id !== id);
    }
  );

  const { fetchWrapper: createAgentSession } = usePromiseWrapper(
    async (payload: CreateAgentSessionPayload) => {
      const { id } = await aiAgentsApiService.createAgentSession(payload);
      return id;
    }
  );

  const { fetchWrapper: promptAgent } = usePromiseWrapper(
    async ({
      prompt,
      files,
      sessionId,
    }: {
      prompt: string;
      files: File[];
      sessionId: number;
      isChat?: boolean;
    }) => {
      agentSessionInfo.value.isSendingPrompt = true;

      if (!sessionId) {
        throw new Error(t("AI_AGENTS.ERROR_MESSAGE.NO_SESSION_ID"));
      }

      const { fileBlocks } = await uploadFiles(files, sessionId);

      const messageContent = buildMessageContent(prompt, fileBlocks);

      if (!messageContent.content?.length) {
        throw new Error(t("AI_AGENTS.ERROR_MESSAGE.NO_CONTENT"));
      }

      await aiAgentsApiService.promptAgent({
        session_id: sessionId.toString(),
        content: messageContent,
      });
    },
    {
      finally: () => {
        agentSessionInfo.value.isSendingPrompt = false;
        agentSessionInfo.value.requestIsLoading = false;
      },
      onError: (error) => {
        throw error;
      },
    }
  );

  const { fetchWrapper: getAgentSession } = usePromiseWrapper(
    async (session_id: string) => {
      const data = await aiAgentsApiService.getAgentSession(session_id);
      agentSessionInfo.value.activeSession = data;
    }
  );

  const { fetchWrapper: getAllAgentSessions } = usePromiseWrapper(
    async ({ agent_id }: { agent_id: string }) => {
      agentSessionInfo.value.previous_sessions =
        await aiAgentsApiService.getAllAgentSessions({
          agent_id,
        });
    }
  );

  const { fetchWrapper: getAllAgentsSessions } = usePromiseWrapper(async () => {
    agentSessionInfo.value.previous_sessions =
      await aiAgentsApiService.getAllAgentsSessions();
  });

  const clearAgentSessionInfo = () => {
    agentSessionInfo.value = getAgentSessionInitialState();
  };

  onMounted(async () => {
    if (options.value) {
      return;
    }

    options.value = await optionsApiService.getAiAgentsOptions();
  });

  return {
    aiAgents,
    isAiAgentsRequestLoading,
    searchMeta,
    getAllIsLoading,
    options,
    agentSessionInfo,
    getAll,
    create,
    update,
    deleteById,
    createAgentSession,
    promptAgent,
    getAgentSession,
    getAllAgentSessions,
    getAllAgentsSessions,
    clearAgentSessionInfo,
  };
});
